/**
 * @module keyboard-shortcuts
 * @description Keyboard shortcuts for power user efficiency
 *
 * Implemented shortcuts:
 * - Ctrl+K / Cmd+K: Focus universal search
 * - Escape: Clear search / Close modals
 * - Ctrl+H / Cmd+H: Navigate to Home
 * - /: Focus file search (when viewing entity)
 * - Arrow keys: Navigate search results
 * - Enter: Select highlighted result
 * - ?: Show keyboard shortcuts help modal
 */

/* global Event */

import { logger } from '../../utils/logger.js';

// ===========================
// State Management
// ===========================

let currentHighlightedIndex = -1;
let searchResultItems = [];

// ===========================
// Keyboard Shortcuts Configuration
// ===========================

const shortcuts = [
    {
        keys: ['Ctrl+K', 'Cmd+K', '⌘K'],
        description: 'Focus universal search',
        action: 'focusUniversalSearch',
        category: 'Navigation'
    },
    {
        keys: ['Escape', 'Esc'],
        description: 'Clear search / Close modals',
        action: 'clearOrClose',
        category: 'Navigation'
    },
    {
        keys: ['Ctrl+H', 'Cmd+H', '⌘H'],
        description: 'Navigate to Home',
        action: 'navigateHome',
        category: 'Navigation'
    },
    {
        keys: ['/'],
        description: 'Focus file search (when viewing entity)',
        action: 'focusFileSearch',
        category: 'Search'
    },
    {
        keys: ['↑', '↓', 'ArrowUp', 'ArrowDown'],
        description: 'Navigate search results',
        action: 'navigateResults',
        category: 'Search'
    },
    {
        keys: ['Enter'],
        description: 'Select highlighted result',
        action: 'selectResult',
        category: 'Search'
    },
    {
        keys: ['?'],
        description: 'Show keyboard shortcuts help',
        action: 'showHelp',
        category: 'Help'
    }
];

// ===========================
// Keyboard Event Handlers
// ===========================

/**
 * Main keyboard event handler
 * @param {KeyboardEvent} event - The keyboard event
 */
function handleKeyDown(event) {
    const key = event.key;
    const ctrlOrCmd = event.ctrlKey || event.metaKey;

    // Ctrl+K or Cmd+K: Focus universal search
    if ((ctrlOrCmd && key === 'k') || (ctrlOrCmd && key === 'K')) {
        event.preventDefault();
        focusUniversalSearch();
        return;
    }

    // Ctrl+H or Cmd+H: Navigate to Home
    if ((ctrlOrCmd && key === 'h') || (ctrlOrCmd && key === 'H')) {
        event.preventDefault();
        navigateHome();
        return;
    }

    // Escape: Clear search / Close modals
    if (key === 'Escape') {
        event.preventDefault();
        clearOrClose();
        return;
    }

    // /: Focus file search (when not in input)
    if (key === '/' && !isInputFocused()) {
        event.preventDefault();
        focusFileSearch();
        return;
    }

    // ?: Show keyboard shortcuts help
    if (key === '?' && !isInputFocused()) {
        event.preventDefault();
        showHelp();
        return;
    }

    // Arrow keys: Navigate search results
    if ((key === 'ArrowUp' || key === 'ArrowDown') && hasSearchResults()) {
        event.preventDefault();
        navigateResults(key === 'ArrowUp' ? -1 : 1);
        return;
    }

    // Enter: Select highlighted result
    if (key === 'Enter' && currentHighlightedIndex >= 0) {
        event.preventDefault();
        selectResult();

    }
}

// ===========================
// Shortcut Actions
// ===========================

/**
 * Focus the universal search input
 */
function focusUniversalSearch() {
    const searchInput = document.getElementById('unified-search-input');
    if (searchInput) {
        searchInput.focus();
        searchInput.select();
        logger.info('🔍 Focused universal search');
    } else {
        logger.warn('Universal search input not found');
    }
}

/**
 * Focus the file search input (when viewing entity)
 */
function focusFileSearch() {
    const fileSearchInput = document.getElementById('file-search-input');
    if (fileSearchInput && !fileSearchInput.closest('.hidden')) {
        fileSearchInput.focus();
        fileSearchInput.select();
        logger.info('🔍 Focused file search');
    } else {
        // Fallback to universal search if file search not available
        focusUniversalSearch();
    }
}

/**
 * Clear search or close modals
 */
function clearOrClose() {
    // Check if help modal is open
    const helpModal = document.getElementById('keyboard-shortcuts-modal');
    if (helpModal && !helpModal.classList.contains('hidden')) {
        closeHelp();
        return;
    }

    // Check if upload modal is open
    const uploadModal = document.getElementById('upload-modal');
    if (uploadModal && !uploadModal.classList.contains('hidden')) {
        uploadModal.classList.add('hidden');
        logger.info('✖️ Closed upload modal');
        return;
    }

    // Clear universal search
    const searchInput = document.getElementById('unified-search-input');
    if (searchInput && searchInput.value) {
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        logger.info('✖️ Cleared search');
        return;
    }

    // Clear file search
    const fileSearchInput = document.getElementById('file-search-input');
    if (fileSearchInput && fileSearchInput.value) {
        fileSearchInput.value = '';
        fileSearchInput.dispatchEvent(new Event('input', { bubbles: true }));
        logger.info('✖️ Cleared file search');
        return;
    }

    logger.debug('Escape pressed but nothing to clear/close');
}

/**
 * Navigate to home page
 */
function navigateHome() {
    window.location.href = window.location.pathname;
    logger.info('🏠 Navigating to home');
}

/**
 * Navigate through search results
 * @param {number} direction - Direction to navigate (-1 for up, 1 for down)
 */
function navigateResults(direction) {
    updateSearchResultItems();

    if (searchResultItems.length === 0) {
        return;
    }

    // Remove previous highlight
    if (currentHighlightedIndex >= 0 && searchResultItems[currentHighlightedIndex]) {
        searchResultItems[currentHighlightedIndex].classList.remove('keyboard-highlighted');
    }

    // Calculate new index
    currentHighlightedIndex += direction;

    // Wrap around
    if (currentHighlightedIndex < 0) {
        currentHighlightedIndex = searchResultItems.length - 1;
    } else if (currentHighlightedIndex >= searchResultItems.length) {
        currentHighlightedIndex = 0;
    }

    // Add new highlight
    const highlightedItem = searchResultItems[currentHighlightedIndex];
    if (highlightedItem) {
        highlightedItem.classList.add('keyboard-highlighted');
        highlightedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        logger.debug(`Highlighted result ${currentHighlightedIndex + 1}/${searchResultItems.length}`);
    }
}

/**
 * Select the currently highlighted result
 */
function selectResult() {
    if (currentHighlightedIndex >= 0 && searchResultItems[currentHighlightedIndex]) {
        const item = searchResultItems[currentHighlightedIndex];

        // For search result items from universal search, click the "View Files" button
        if (item.classList.contains('result-item')) {
            const viewFilesBtn = item.querySelector('.btn-primary');
            if (viewFilesBtn && viewFilesBtn.textContent.includes('View Files')) {
                viewFilesBtn.click();
                logger.info('✅ Selected search result view files button');
                resetResultNavigation();
                return;
            }
        }

        // For file items, prefer clicking the view button if available
        if (item.classList.contains('file-item')) {
            const viewButton = item.querySelector('.view-btn');
            if (viewButton) {
                viewButton.click();
                logger.info('✅ Selected file view button');
                resetResultNavigation();
                return;
            }
        }

        // For entity type cards, recent items, or fallback
        item.click();
        logger.info('✅ Selected highlighted result');

        resetResultNavigation();
    }
}

/**
 * Show keyboard shortcuts help modal
 */
function showHelp() {
    let modal = document.getElementById('keyboard-shortcuts-modal');

    if (!modal) {
        modal = createHelpModal();
        document.body.appendChild(modal);
    }

    modal.classList.remove('hidden');
    logger.info('❓ Showing keyboard shortcuts help');
}

/**
 * Close keyboard shortcuts help modal
 */
function closeHelp() {
    const modal = document.getElementById('keyboard-shortcuts-modal');
    if (modal) {
        modal.classList.add('hidden');
        logger.info('✖️ Closed keyboard shortcuts help');
    }
}

// ===========================
// Helper Functions
// ===========================

/**
 * Check if an input element is currently focused
 * @returns {boolean} True if input is focused
 */
function isInputFocused() {
    const activeElement = document.activeElement;
    return activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
    );
}

/**
 * Check if there are search results available
 * @returns {boolean} True if search results exist
 */
function hasSearchResults() {
    updateSearchResultItems();
    return searchResultItems.length > 0;
}

/**
 * Update the list of search result items
 */
function updateSearchResultItems() {
    // Universal search results
    const universalResults = document.querySelectorAll('.result-item:not(.hidden)');

    // Entity type cards - only if parent container is visible
    const entityCards = Array.from(document.querySelectorAll('.entity-type-card:not(.hidden)'))
        .filter(card => {
            // Check if card is inside collapsed browse-content
            const browseContent = card.closest('#browse-content');
            if (browseContent && browseContent.classList.contains('collapsed')) {
                return false;
            }

            // Check for hidden parents
            let parent = card.parentElement;
            while (parent && parent !== document.body) {
                if (parent.classList.contains('hidden') ||
                    parent.style.display === 'none' ||
                    window.getComputedStyle(parent).display === 'none') {
                    return false;
                }
                parent = parent.parentElement;
            }
            return true;
        });

    // Recent items
    const recentItems = document.querySelectorAll('.recent-item-card:not(.hidden)');

    // File items (don't include view buttons separately, they're inside file-item)
    const fileItems = document.querySelectorAll('.file-item:not(.hidden)');

    // Combine all navigable items
    searchResultItems = [
        ...universalResults,
        ...entityCards,
        ...recentItems,
        ...fileItems
    ];
}

/**
 * Reset result navigation state
 */
function resetResultNavigation() {
    // Remove all highlights
    searchResultItems.forEach(item => {
        item.classList.remove('keyboard-highlighted');
    });

    currentHighlightedIndex = -1;
    searchResultItems = [];
}

/**
 * Create the keyboard shortcuts help modal
 * @returns {HTMLElement} The modal element
 */
function createHelpModal() {
    const modal = document.createElement('div');
    modal.id = 'keyboard-shortcuts-modal';
    modal.className = 'keyboard-shortcuts-modal hidden';

    // Group shortcuts by category
    const categories = {};
    shortcuts.forEach(shortcut => {
        if (!categories[shortcut.category]) {
            categories[shortcut.category] = [];
        }
        categories[shortcut.category].push(shortcut);
    });

    let shortcutsHTML = '';
    Object.entries(categories).forEach(([category, items]) => {
        shortcutsHTML += `
            <div class="shortcuts-category">
                <h4>${category}</h4>
                <div class="shortcuts-list">
        `;

        items.forEach(shortcut => {
            const keysHTML = shortcut.keys.map(key =>
                `<kbd class="keyboard-key">${key}</kbd>`
            ).join(' or ');

            shortcutsHTML += `
                <div class="shortcut-item">
                    <div class="shortcut-keys">${keysHTML}</div>
                    <div class="shortcut-description">${shortcut.description}</div>
                </div>
            `;
        });

        shortcutsHTML += `
                </div>
            </div>
        `;
    });

    modal.innerHTML = `
        <div class="keyboard-shortcuts-overlay"></div>
        <div class="keyboard-shortcuts-content">
            <div class="keyboard-shortcuts-header">
                <h3>⌨️ Keyboard Shortcuts</h3>
                <button class="close-shortcuts-btn" aria-label="Close">✕</button>
            </div>
            <div class="keyboard-shortcuts-body">
                ${shortcutsHTML}
            </div>
            <div class="keyboard-shortcuts-footer">
                <p>Press <kbd>?</kbd> to toggle this help</p>
            </div>
        </div>
    `;

    // Add event listeners
    const closeBtn = modal.querySelector('.close-shortcuts-btn');
    const overlay = modal.querySelector('.keyboard-shortcuts-overlay');

    closeBtn.addEventListener('click', closeHelp);
    overlay.addEventListener('click', closeHelp);

    return modal;
}

// ===========================
// Initialization
// ===========================

/**
 * Initialize keyboard shortcuts
 */
export function initializeKeyboardShortcuts() {
    // Add global keyboard event listener
    document.addEventListener('keydown', handleKeyDown);

    // Reset navigation when search results change
    const searchInput = document.getElementById('unified-search-input');
    if (searchInput) {
        searchInput.addEventListener('input', resetResultNavigation);
    }

    const fileSearchInput = document.getElementById('file-search-input');
    if (fileSearchInput) {
        fileSearchInput.addEventListener('input', resetResultNavigation);
    }

    logger.info('⌨️ Keyboard shortcuts initialized');
}

/**
 * Cleanup keyboard shortcuts (for testing/cleanup)
 */
export function cleanupKeyboardShortcuts() {
    document.removeEventListener('keydown', handleKeyDown);
    resetResultNavigation();

    const modal = document.getElementById('keyboard-shortcuts-modal');
    if (modal) {
        modal.remove();
    }

    logger.info('⌨️ Keyboard shortcuts cleaned up');
}

// Export for testing
export { shortcuts, showHelp, closeHelp };
